import { PaymentService } from '../services/payment.service'
import type { CreateOrderRequest, CreateTrialSetupIntentRequest, PaymentVerificationRequest } from '../types/payment.types'

let paymentServiceInstance: PaymentService | null = null

/**
 * Payment Service Composable
 * Provides access to payment API operations
 */
export const usePaymentService = () => {
  // Create singleton instance
  if (!paymentServiceInstance) {
    paymentServiceInstance = new PaymentService()
  }

  const service = paymentServiceInstance

  /**
   * Create a payment order
   */
  const createOrder = async (data: CreateOrderRequest) => {
    return await service.createOrder(data)
  }

  /**
   * Verify payment after redirect
   */
  const verifyPayment = async (data: PaymentVerificationRequest) => {
    return await service.verifyPayment(data)
  }

  /**
   * Get payment methods for a contact
   */
  const getPaymentMethods = async (contactId: string) => {
    return await service.getPaymentMethods(contactId)
  }

  /**
   * Cancel a subscription
   */
  const cancelSubscription = async (subscriptionId: string, reason?: string) => {
    return await service.cancelSubscription(subscriptionId, reason)
  }

  /**
   * Update subscription
   */
  const updateSubscription = async (subscriptionId: string, data: Record<string, unknown>) => {
    return await service.updateSubscription(subscriptionId, data)
  }

  /**
   * Get subscription details
   */
  const getSubscription = async (subscriptionId: string) => {
    return await service.getSubscription(subscriptionId)
  }

  /**
   * Create a Stripe Setup Intent for trial signup (client secret for Payment Element)
   */
  const createTrialSetupIntent = async (data: CreateTrialSetupIntentRequest) => {
    return await service.createTrialSetupIntent(data)
  }

  /**
   * Initiate payment for communities
   */
  const initiatePayment = async (contactId: string, orderId: string, sourceId: string) => {
    return await service.initiatePayment(contactId, orderId, sourceId)
  }

  /**
   * Capture payment for communities
   */
  const capturePayment = async (contactId: string, orderId: string, sourceId: string) => {
    return await service.capturePayment(contactId, orderId, sourceId)
  }

  /**
   * Get default payment provider for a location
   */
  const getDefaultPaymentProvider = async (locationId: string, liveMode: boolean) => {
    return await service.getDefaultPaymentProvider(locationId, liveMode)
  }

  return {
    createOrder,
    createTrialSetupIntent,
    verifyPayment,
    getPaymentMethods,
    cancelSubscription,
    updateSubscription,
    getSubscription,
    initiatePayment,
    capturePayment,
    getDefaultPaymentProvider,
  }
}
